<template>
  <div>
    <div class="headline5 mb-4">
      {{ $t(`modals.addGlobalMspPolicy.policies.endpointDataGovernance.title`) }}
    </div>
    <dlp-settings-content
      :action-not-allowed="disabled"
      :other-data-types="otherDataTypes"
      :recommended-data-types="recommendedDataTypes"
      :model-value="localValue"
      @set-parameters="onSetParametersClick"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, type PropType, onMounted, type Ref } from "vue";
import { defaultEmptyPolicies, GlobalPolicySection } from "@/constants/msp.ts";
import cloneDeep from "lodash/cloneDeep";
import type { GlobalPolicyConfig } from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import DlpSettingsContent from "@/components/settings/DlpSettingsContent.vue";
import { axiosInstance } from "@/plugins/https.ts";
import api from "@/_helpers/api.ts";
import { getRecommendedAndOtherDataTypes } from "@/_store/user-data-governance/adapter.ts";
import { EndpointDataGovernanceAction } from "@/constants/endpoint-data-governance.ts";
import SetIndustryModal from "@/components/modals/data-governance/SetIndustryModal.vue";
import { ModalWidth } from "@/constants/modals.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { componentDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

export default defineComponent({
  components: { DlpSettingsContent },
  props: {
    data: {
      type: Object as PropType<GlobalPolicyConfig>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:data"],
  setup(props, { emit }) {
    const dialogsStore = useDialogsStore();
    const { openDialog } = dialogsStore;
    const localValue = ref(
      cloneDeep(
        props.data[GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE] ??
          defaultEmptyPolicies[GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE]
      )
    );

    const recommendedDataTypes: Ref<string[]> = ref([]);
    const otherDataTypes: Ref<string[]> = ref([]);

    async function getRecommendedAndOtherDataTypesData(industries: string[]) {
      try {
        const { data } = await axiosInstance.request({
          ...api.privacyParametersConfig(),
          params: {
            industries: industries.join(","),
          },
        });
        const dataTypes = getRecommendedAndOtherDataTypes(data);
        return {
          recommendedDataTypes: dataTypes.recommendedDataTypes,
          otherDataTypes: dataTypes.otherDataTypes,
          settings: data,
        };
      } catch (error) {
        console.error(error);
        return {
          recommendedDataTypes: [],
          otherDataTypes: [],
          settings: null,
        };
      }
    }

    onMounted(async () => {
      const result = await getRecommendedAndOtherDataTypesData(
        localValue.value.wizardParameters.industries
      );
      recommendedDataTypes.value = result.recommendedDataTypes;
      otherDataTypes.value = result.otherDataTypes;
    });

    const onSetParametersClick = () => {
      const dialogConfig = {
        ...componentDialogsConfigConstructor({
          action: EndpointDataGovernanceAction.SET_INDUSTRY,
          component: SetIndustryModal,
          width: ModalWidth.LARGE,
          disable: false,
          item: localValue.value?.wizardParameters.isDefault
            ? { selectedIndustries: [] }
            : localValue.value?.wizardParameters,
          callback: async (val: { selectedIndustries: string[] }) => {
            const result = await getRecommendedAndOtherDataTypesData(val.selectedIndustries);
            recommendedDataTypes.value = result.recommendedDataTypes;
            otherDataTypes.value = result.otherDataTypes;
            localValue.value = result.settings;
          },
        }),
      };

      openDialog(dialogConfig);
    };

    watch(
      localValue,
      (newVal) => {
        emit("update:data", {
          ...props.data,
          [GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE]: newVal,
        });
      },
      { immediate: true, deep: true }
    );

    return {
      localValue,
      recommendedDataTypes,
      onSetParametersClick,
      otherDataTypes,
    };
  },
});
</script>

<style scoped lang="scss"></style>
